import React, { useState, useEffect } from 'react';
import { Tabs, Table, Button, Modal, Form, Input, Card, message, DatePicker, Statistic, Row, Col, Tag } from 'antd';
import { smsService } from '../api/sms.service';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

// Basic UI Placeholder for SMS Settings
export const SmsSettings: React.FC = () => {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <h2>SMS / MSG91 Configuration</h2>
            <Tabs defaultActiveKey="templates" items={[
                {
                    key: 'templates',
                    label: 'Templates',
                    children: <TemplateManagement />
                },
                {
                    key: 'logs',
                    label: 'SMS Logs',
                    children: <SmsLogs />
                },
                {
                    key: 'analytics',
                    label: 'Analytics',
                    children: <SmsAnalytics />
                }
            ]} />
        </div>
    );
};

// Template Management Component
const TemplateManagement: React.FC = () => {
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [isAddVersionModalVisible, setIsAddVersionModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [versionForm] = Form.useForm();
    const [loading, setLoading] = useState(false);
    
    // Live Version Inspection State
    const [lookupId, setLookupId] = useState('');
    const [activeTemplateMsg91Id, setActiveTemplateMsg91Id] = useState('');
    const [templateVersions, setTemplateVersions] = useState<any[]>([]);
    const [fetchingVersions, setFetchingVersions] = useState(false);

    const handleLookupVersions = async (msg91TemplateId: string) => {
        if (!msg91TemplateId) {
            message.warning('Please enter a MSG91 Template ID.');
            return;
        }
        
        setActiveTemplateMsg91Id(msg91TemplateId);
        setFetchingVersions(true);
        
        try {
            const response = await smsService.getTemplateVersions(msg91TemplateId);
            // MSG91 returns the array inside response.data.data
            setTemplateVersions(response.data?.data?.data || []);
        } catch (error) {
            message.error('Failed to load MSG91 Template Versions.');
            setTemplateVersions([]);
        } finally {
            setFetchingVersions(false);
        }
    };

    const handleCreateTemplate = async (values: any) => {
        setLoading(true);
        try {
            const resp = await smsService.createTemplate({
                name: values.name,
                content: values.content,
                dlt_id: values.dlt_id
            });
            message.success('Template deployed to MSG91!');
            setIsCreateModalVisible(false);
            form.resetFields();
            
            // If the provider returned the ID, we can automatically select it
            if (resp.data?.data?.template_id || resp.data?.data?.auth_id) {
                const newId = resp.data.data.template_id || resp.data.data.auth_id;
                setLookupId(newId);
                handleLookupVersions(newId);
            }
        } catch (error) {
            message.error('Failed to create template via MSG91');
        } finally {
            setLoading(false);
        }
    };

    const handleAddVersion = async (values: any) => {
        setLoading(true);
        try {
            await smsService.createTemplateVersion(activeTemplateMsg91Id, {
                content: values.content,
                dlt_id: values.dlt_id
            });
            message.success('New template version submitted to MSG91 for approval.');
            setIsAddVersionModalVisible(false);
            versionForm.resetFields();
            handleLookupVersions(activeTemplateMsg91Id);
        } catch (error) {
            message.error('Failed to add template version via MSG91');
        } finally {
            setLoading(false);
        }
    };

    const handleMarkDefault = async (versionId: string) => {
        try {
            await smsService.markVersionDefault(activeTemplateMsg91Id, versionId);
            message.success(`Version ${versionId} marked as default.`);
            handleLookupVersions(activeTemplateMsg91Id);
        } catch (error) {
            message.error('Failed to mark version as default.');
        }
    };

    return (
        <Card 
            title="MSG91 SMS Templates Engine" 
            extra={
                <Button type="primary" onClick={() => setIsCreateModalVisible(true)}>Create New Base Template</Button>
            }
        >
            <div style={{ marginBottom: 24, display: 'flex', gap: 16 }}>
                <Input.Search 
                    placeholder="Enter MSG91 Template ID to manage..." 
                    value={lookupId}
                    onChange={(e) => setLookupId(e.target.value)}
                    onSearch={handleLookupVersions}
                    enterButton="Inspect Template"
                    style={{ maxWidth: 450 }}
                />
            </div>

            {activeTemplateMsg91Id && (
                <Card type="inner" title={`Template Inspector: ${activeTemplateMsg91Id}`} extra={
                    <Button type="dashed" onClick={() => setIsAddVersionModalVisible(true)}>Submit New Version</Button>
                }>
                    <Table 
                        dataSource={templateVersions} 
                        rowKey="id" 
                        loading={fetchingVersions}
                        pagination={false}
                        columns={[
                        { title: 'Local Version ID', dataIndex: 'version' },
                        { title: 'MSG91 API ID', dataIndex: 'id' },
                        { title: 'Name', dataIndex: 'template_name' },
                        { title: 'Status', dataIndex: 'status', render: (val) => (val === '1' ? <Tag color="success">Approved</Tag> : <Tag color="warning">Pending/Rejected</Tag>) },
                        { title: 'Active', dataIndex: 'active_status', render: (val) => (val === '1' ? <Tag color="blue">Active</Tag> : <Tag color="default">Inactive</Tag>) },
                        { title: 'Content', dataIndex: 'template_data', ellipsis: true },
                        { title: 'Actions', render: (_, record) => (
                            <Button size="small" type="link" onClick={() => handleMarkDefault(record.id)} disabled={record.status !== '1'}>
                                Set as Default
                            </Button>
                        )}
                    ]} />
                </Card>
            )}

            <Modal title="Create New Base Template" open={isCreateModalVisible} onCancel={() => setIsCreateModalVisible(false)} onOk={() => form.submit()} confirmLoading={loading}>
                <Form form={form} layout="vertical" onFinish={handleCreateTemplate}>
                    <Form.Item name="name" label="Private Name Tracker" rules={[{ required: true }]}>
                        <Input placeholder="e.g., General Information" />
                    </Form.Item>
                    <Form.Item name="content" label="Template Content" rules={[{ required: true }]} extra="Use ##variable## for merge tags.">
                        <Input.TextArea rows={4} placeholder="Hello ##name##..." />
                    </Form.Item>
                    <Form.Item name="dlt_id" label="DLT PE ID (India Only)">
                        <Input placeholder="Optional DLT Registration ID" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal title={`Add Version to ${activeTemplateMsg91Id}`} open={isAddVersionModalVisible} onCancel={() => setIsAddVersionModalVisible(false)} onOk={() => versionForm.submit()} confirmLoading={loading}>
                <Form form={versionForm} layout="vertical" onFinish={handleAddVersion}>
                    <Form.Item name="content" label="Template Content" rules={[{ required: true }]} extra="Warning: Requires MSG91 manual approval.">
                        <Input.TextArea rows={4} placeholder="Updated message content here..." />
                    </Form.Item>
                    <Form.Item name="dlt_id" label="DLT PE ID (India Only)">
                        <Input placeholder="Optional DLT Registration ID" />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

// Logs Component
const SmsLogs: React.FC = () => {
    const [logs, setLogs] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    
    const fetchLogs = async (dates: any) => {
        if (!dates || dates.length !== 2) return;
        setLoading(true);
        try {
            const startStr = dates[0].format('YYYY-MM-DD');
            const endStr = dates[1].format('YYYY-MM-DD');
            const response = await smsService.getLogs({ start_date: startStr, end_date: endStr, limit: 100 });
            
            // MSG91 nests the list inside data.data
            setLogs(response.data?.data?.data || []);
        } catch (error) {
            message.error('Failed to load SMS logs.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs([dayjs().subtract(2, 'd'), dayjs()]);
    }, []);

    return (
        <Card title="SMS Delivery Logs" extra={<RangePicker defaultValue={[dayjs().subtract(2, 'd'), dayjs()]} onChange={(dates) => fetchLogs(dates)} />}>
            <Table dataSource={logs} rowKey={(record, index) => record?.requestId || index} loading={loading} columns={[
                { title: 'Request ID', dataIndex: 'requestId' },
                { title: 'Mobile', dataIndex: 'telNum' },
                { title: 'Status', dataIndex: 'status' },
                { title: 'Sent At', dataIndex: 'requestDate' },
            ]} />
        </Card>
    );
};

// Analytics Component
const SmsAnalytics: React.FC = () => {
    const [metrics, setMetrics] = useState<any>({ 
        message: 0, delivered: 0, failed: 0, rejected: 0, ndnc: 0,
        totalCredits: 0, failedCredits: 0, deliveredCredits: 0 
    });
    const [loading, setLoading] = useState(false);

    const fetchAnalytics = async (dates: any) => {
        if (!dates || dates.length !== 2) return;
        setLoading(true);
        try {
            const startStr = dates[0].format('YYYY-MM-DD');
            const endStr = dates[1].format('YYYY-MM-DD');
            const response = await smsService.getAnalytics({ start_date: startStr, end_date: endStr });
            setMetrics(response.data?.data?.total || { 
                message: 0, delivered: 0, failed: 0, rejected: 0, ndnc: 0,
                totalCredits: 0, failedCredits: 0, deliveredCredits: 0 
            });
        } catch (error) {
            message.error('Failed to load Analytics.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics([dayjs().subtract(7, 'd'), dayjs()]);
    }, []);

    return (
        <Card title="MSG91 SMS Analytics" extra={<RangePicker defaultValue={[dayjs().subtract(7, 'd'), dayjs()]} onChange={(dates) => fetchAnalytics(dates)} />}>
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                    <Card loading={loading}><Statistic title="Total Messages" value={metrics.message} /></Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card loading={loading}><Statistic title="Delivered" value={metrics.delivered} valueStyle={{ color: '#3f8600' }} /></Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card loading={loading}><Statistic title="Failed" value={metrics.failed} valueStyle={{ color: '#cf1322' }} /></Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card loading={loading}><Statistic title="Rejected" value={metrics.rejected} valueStyle={{ color: '#fa8c16' }} /></Card>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                    <Card loading={loading}><Statistic title="NDNC Blocked" value={metrics.ndnc} /></Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card loading={loading}><Statistic title="Total Credits" value={metrics.totalCredits} precision={2} /></Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card loading={loading}><Statistic title="Delivered Credits" value={metrics.deliveredCredits} precision={2} valueStyle={{ color: '#3f8600' }} /></Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card loading={loading}><Statistic title="Failed Credits" value={metrics.failedCredits} precision={2} valueStyle={{ color: '#cf1322' }} /></Card>
                </Col>
            </Row>
        </Card>
    );
};
